import { Injector } from "@angular/core";
import { Observable, of, from, empty } from "rxjs";
import { concatMap, map } from "rxjs/operators";
import { loadModules } from "esri-loader";
import esri = __esri;

import { AreaOfReviewPoint } from "../../../../gis/models/odnr-map-point.model";
import { Renderer, MAP_ELEMENT, CONFIG } from "./renderer.interface";
import { Renderer2D } from "./Renderer-2D.service";
import { VIEW_WIDGETS } from "../models/widgets.model";




export const QTR_MILE = "InjectionWell_AOR_qtrmile";
export const HALF_MILE = "InjectionWell_AOR_halfmile"


export class AreaOfReviewMapRenderer2D extends Renderer<esri.MapView> {   
    protected map: esri.Map;
    public _points: AreaOfReviewPoint[] = [];

    defaultConfig() {
        return {
            properties: {
                basemap: this.baseMap,
                zoom: 18,
                center: [0, 0]
            } as esri.MapProperties,
            widgets: []
        };
    }

    constructor(points: AreaOfReviewPoint[], injector: Injector) {
        super(injector);
        this._points = points;
        this.config = Object.assign(this.defaultConfig(), injector.get(CONFIG, this.defaultConfig()));
        this.config.properties = Object.assign(this.defaultConfig().properties, this.config.properties);
        this.containerElement = injector.get(MAP_ELEMENT);
    }

    initialize(): Observable<esri.MapView> {
        this.config = Object.assign(this.defaultConfig(), this.injector.get(CONFIG, this.defaultConfig()));
        this.config.properties = Object.assign(this.defaultConfig().properties, this.config.properties);

        return from(loadModules([
            "esri/Map",
            "esri/views/MapView",
        ])).pipe(concatMap(([EsriMap, MapView]) => {
            this.map = new EsriMap(this.config.properties);
            let mapProps = {
                container: this.containerElement,
                center: this.config.properties.center,
                zoom: this.config.properties.zoom,
                map: this.map,
                constraints: {
                    snapToZoom: false,
                    rotationEnabled: false
                }
            };

            this._view = new MapView(mapProps);

            return this.createWidgets().pipe(map(() => this._view));
        }));
    }


    protected createWidgets(): Observable<any> {
        if (this.config.widgets && this._view) {
            return from(loadModules([
                "esri/widgets/BasemapToggle",
                "esri/widgets/LayerList",
                "esri/widgets/Search",
                "esri/widgets/Compass"
            ])).pipe(map(([BasemapToggle, LayerList, Search, Compass]) => {
                this.config.widgets.forEach(x => {
                    let widget = null;
                    switch (x.type) {
                        case VIEW_WIDGETS.basemapToggle:
                            widget = new BasemapToggle({
                                view: this._view
                            });
                            break;
                        case VIEW_WIDGETS.compass:
                            widget = new Compass({
                                view: this._view
                            });
                            break;
                        case VIEW_WIDGETS.layerList:
                            widget = new LayerList({
                                view: this._view,
                                listItemCreatedFunction: function (event) {
                                    setTimeout(function () {
                                        const item = event.item;
                                        const itemUId = `${widget.id}_${item.uid}__title`;
                                        const node = document.getElementById(itemUId);
                                        if (node) {
                                            node.style.color = `rgb(${item.layer.graphics.items[0].symbol.color.r}, ${item.layer.graphics.items[0].symbol.color.g}, ${item.layer.graphics.items[0].symbol.color.b})`; //done to eliminate any opacity dimension from the existing color
                                            node.style.fontSize = "8pt";
                                            if (node.parentElement) {
                                                const parent = node.parentElement;
                                                if (parent.parentElement) {
                                                    const grandparent = parent.parentElement;
                                                    grandparent.style.padding = "0px";
                                                }
                                            }
                                        }
                                    }, 50);
                                }
                            });
                            break;
                        default:
                            console.debug("widget type not supported.");
                            break;
                    }
                    this._view.ui.add(widget, x.position);
                });
            }));
        } else {
            return empty();
        }
    }

    export(): Observable<any> {
        return from(this.view.takeScreenshot());
    }

    render(): Observable<any> {
        return this.generateLayers().pipe(concatMap((layers) => {
            this.map.addMany(layers);

            this._view.when(() => {
                this._view.popup.featureNavigationEnabled = false;
                this._view.popup.dockEnabled = false;
            });

            return of(layers);
        }));
    }

    public generateLayers(): Observable<any> {
        return from(loadModules([
            "esri/layers/GraphicsLayer",
            "esri/Graphic",
            "esri/geometry/Circle",
            "esri/geometry/Point",
            "esri/geometry/geometryEngine"
        ])).pipe(concatMap(([GraphicsLayer, Graphic, Circle, Point, geometryEngine]) => {
            const layers: any[] = [];
            for (let point of this._points) {
                const center = new Point({
                    longitude: point.longitude,
                    latitude: point.latitude
                });

                let existingLayer: any;
                let markerSymbol = {};
                let formation = (point.producingFormation) ? point.producingFormation : "Unknown Formation / Undrilled";
                if (point.type === "WellHead") {
                    markerSymbol = {
                        type: "simple-marker",
                        color: [226, 119, 40],
                        outline: { color: [226, 119, 40], width: 2 }
                    };

                    let proposedLayer = new GraphicsLayer({
                        id: "proposedLayer",
                        name: "Proposed Well Head",
                        title: "Proposed Well Head"
                    });
                    const quarterCircleLayer = new GraphicsLayer({ id: "qtr", name: "Quarter-Mile AOR", title: "Quarter-Mile Area of Review", unit: 'miles' });
                    const halfCircleLayer = new GraphicsLayer({ id: "half", name: "Half-Mile AOR", title: "Half-Mile Area of Review", unit: 'miles' });

                    halfCircleLayer.add(new Graphic({
                        geometry: geometryEngine.geodesicBuffer(center, .5, 'miles'),
                        symbol: {
                            type: "simple-fill", color: [75, 0, 130, 0.5], outline: {
                                color: [128, 128, 128, 0.5],
                                width: "0.5px"
                            }
                        }
                    }));

                    quarterCircleLayer.add(new Graphic({
                        geometry: geometryEngine.geodesicBuffer(center, .25, 'miles'),
                        symbol: {
                            type: "simple-fill", color: [0, 0, 255, 0.5], outline: {
                                color: [128, 128, 128, 0.5],
                                width: "0.5px"
                            }
                        }
                    }));

                    proposedLayer.add(new Graphic({
                        geometry: center,
                        symbol: markerSymbol,
                        popupTemplate: this.getPopupTemplate(point)
                    }));
                    if (this.config.actionRadius === ActionRadius.HalfMile && layers.findIndex(l => l.id == "half") === -1) {
                        layers.push(halfCircleLayer);
                    }
                    if (this.config.actionRadius === ActionRadius.QuarterMile && layers.findIndex(l => l.id == "qtr") === -1) {
                        layers.push(quarterCircleLayer);
                    }
                    layers.push(proposedLayer);
                } else {
                    existingLayer = layers.find(ly => ly.id === formation);
                    let color = (existingLayer) ? existingLayer.graphics.items[0].symbol.color : this.getNextColor(layers.length);
                    markerSymbol = {
                        type: "simple-marker",
                        color: color,
                        outline: { color: "red", width: .5 }
                    }
                    const pointGraphic = new Graphic({
                        geometry: center,
                        symbol: markerSymbol,
                        popupTemplate: this.getPopupTemplate(point)
                    });
                    if (existingLayer) {
                        existingLayer.add(pointGraphic);
                    } else {
                        let newLayer = new GraphicsLayer({
                            id: formation,
                            name: formation,
                            title: formation
                        });
                        newLayer.add(pointGraphic);
                        layers.push(newLayer);
                    }
                }

            }
            return of(layers);
        }));
    }

    public getNextColor(index: number): number[] {
        const next = index % 6;
        let rgb: number[] = [];
        switch (next) {
            case 0:
                rgb = [166, 195, 107];
                break;
            case 1:
                rgb = [255, 165, 0];
                break;
            case 2:
                rgb = [9, 139, 188];
                break;
            case 3:
                rgb = [182, 62, 46];
                break;
            case 4:
                rgb = [128, 0, 128];
                break;
            case 5:
                rgb = [255, 235, 153];
                break;
        }
        return rgb;
    }

    getPopupTemplate(data: AreaOfReviewPoint): any {
        const title = `${data.wellName}`;
        let content = "";
        if (data.type === "WellHead") {
            content = `Latitude: <b>${data.latitude}</b><br />Longitude: <b>${data.longitude}</b>`;
        } else {
            content = `<span style='font-weight: bold; color: red'>Corrective Action Required</span><br />API Number: <b>${data.APINumber}</b><br />Owner Name: <b>${data.orgName}</b><br />Well Type: <b>${data.wellType}</b><br />Total Depth: <b>${data.totalDepth}</b><br />Formation(s): <b>${data.producingFormation}</b><br />Latitude: <b>${data.latitude}</b><br />Longitude: <b>${data.longitude}</b>`
        }
        return {
            title: title,
            content: content,
            dockOptions: { buttonEnabled: false },
            overwriteActions: true,
            actions: []
        };
    }
}

export enum ActionRadius {
    QuarterMile = .25,
    HalfMile = .5
}
